import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from '../../../hooks/useTranslation'
import { validateTeviId, submitMCNApplication } from '../../../services/api'
import { BENEFIT_KEYS, HERO_ILLUSTRATION, STEP_KEYS, STATUS_MAP, DEBOUNCE_DELAY } from '../constant'
import useTeviSdk from './useTeviSdk'
import { debugLog } from '../../../components/ui/debugPopup'

const useHome = () => {
  const { getUserInfo } = useTeviSdk()

  const { t, currentLanguage, changeLanguage, isReady } = useTranslation()

  // View state: 'landing' | 'form' | 'success'
  const [currentView, setCurrentView] = useState('landing')

  // Form state
  const [teviId, setTeviId] = useState('')
  const [inputStatus, setInputStatus] = useState('default')
  const [statusMessage, setStatusMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [showFindIdModal, setShowFindIdModal] = useState(false)

  // User info from SDK (refs - only used internally, no re-render needed)
  const userAppTokenRef = useRef(null)
  const sdkUserIdRef = useRef(null)

  // Ref for debounce cleanup
  const validationTimeoutRef = useRef(null)

  // Get user info from Tevi SDK on mount (webview)
  useEffect(() => {
    getUserInfo().then((userInfo) => {
      debugLog('SDK getUserInfo response', userInfo)
      const sdkUser = userInfo?.userInfo || {}
      const token = sdkUser.user_app_token || ''

      // Extract user_id: try direct field first, then decode from JWT token
      let userId = sdkUser.user_id ? String(sdkUser.user_id) : ''
      if (!userId && token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          if (payload.user_id) userId = String(payload.user_id)
        } catch { /* invalid token */ }
      }

      debugLog('Extracted userId', userId)
      userAppTokenRef.current = token
      if (userId) {
        sdkUserIdRef.current = userId
        setTeviId(userId)
      }
    })
  }, [])

  // Debounced validation (skip when SDK already provided user info)
  useEffect(() => {
    if (sdkUserIdRef.current && sdkUserIdRef.current === teviId) {
      setInputStatus('default')
      setStatusMessage('')
      return
    }

    if (!teviId.trim()) {
      setInputStatus('default')
      setStatusMessage('')
      return
    }

    setIsValidating(true)

    validationTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await validateTeviId(teviId)
        setInputStatus(STATUS_MAP[result.status])
        setStatusMessage(result.message)
      } catch {
        setInputStatus('error')
        setStatusMessage(t('validation.validationFailed'))
      } finally {
        setIsValidating(false)
      }
    }, DEBOUNCE_DELAY)

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [teviId, t])

  const handleClear = useCallback(() => {
    setTeviId('')
    setInputStatus('default')
    setStatusMessage('')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (inputStatus === 'error' || inputStatus === 'success' || !teviId.trim()) {
      return
    }

    setIsSubmitting(true)
    try {
      const response = await submitMCNApplication({ teviId, token: userAppTokenRef.current })
      debugLog('Submit response', response)
      if (response.success) {
        setCurrentView('success')
      } else {
        setInputStatus('error')
        setStatusMessage(response.message)
      }
    } catch {
      setInputStatus('error')
      setStatusMessage(t('validation.somethingWrong'))
    } finally {
      setIsSubmitting(false)
    }
  }, [inputStatus, teviId, t])

  const handleReset = useCallback(() => {
    setTeviId('')
    setInputStatus('default')
    setStatusMessage('')
    setCurrentView('landing')
  }, [])

  const handleOpenFindIdModal = useCallback(() => {
    setShowFindIdModal(true)
  }, [])

  const handleCloseFindIdModal = useCallback(() => {
    setShowFindIdModal(false)
  }, [])

  const isDisabled = useMemo(() => !teviId.trim() || isValidating || inputStatus === 'error' || inputStatus === 'success', [teviId, isValidating, inputStatus])

  return {
    isReady,
    currentView,
    t,
    benefitKeys: BENEFIT_KEYS,
    heroIllustration: HERO_ILLUSTRATION,
    stepKeys: STEP_KEYS,
    currentLanguage,
    changeLanguage,
    teviId,
    setTeviId,
    inputStatus,
    statusMessage,
    isSubmitting,
    isDisabled,
    handleClear,
    handleSubmit,
    handleReset,
    showFindIdModal,
    handleOpenFindIdModal,
    handleCloseFindIdModal,
  }
}

export default useHome
